/**
 * Template Form Model
 *
 * The pure half of the workout / plan form's exercise editor, shared by web and
 * mobile: how a set's fields are labelled, the tracking-type pill, the quick
 * set schemes, and the operations that add, edit and remove sets. The
 * components that draw these live in each platform's `template-form` kit.
 */

import {
  hasAtMostDecimalPlaces,
  SET_VALUE_DECIMAL_PLACES,
} from '../../validation/template.schemas';
import { getFieldsForWorkoutExerciseTrackingType } from '../measurement';

import type { MeasurementSystem } from '../../types/auth';
import type {
  SetField,
  SetFieldConfig,
  WorkoutExerciseTrackingType,
  WorkoutSet,
} from '../../types/domain/workout.types';
import type { TemplateSetOutput } from '../../validation/template.schemas';

type TrackingType = WorkoutExerciseTrackingType;

// =============================================================================
// FORM-SPECIFIC FIELD CONFIGS (uppercase units for compact UI labels)
// =============================================================================

/**
 * Field configurations for SetRow form inputs.
 * Uses uppercase units for compact label display in form UI.
 */
export const SET_FIELD_CONFIGS: Record<SetField, SetFieldConfig> = {
  weight: {
    field: 'weight',
    unit: { metric: 'KG', imperial: 'LB' },
    inputMode: 'decimal',
    placeholder: '0',
  },
  reps: {
    field: 'reps',
    unit: { metric: 'REPS', imperial: 'REPS' },
    inputMode: 'numeric',
    placeholder: '0',
  },
  duration: {
    field: 'duration',
    unit: { metric: 'SEC', imperial: 'SEC' },
    inputMode: 'decimal',
    placeholder: '0',
  },
  distance: {
    field: 'distance',
    unit: { metric: 'KM', imperial: 'MI' },
    inputMode: 'decimal',
    placeholder: '0',
  },
};

/**
 * How many decimal places a set value may have: reps are counted, so none;
 * weight, duration and distance are measured, to three places like the API.
 */
export function getSetValueDecimalPlaces(field: SetField): number {
  return field === 'reps' ? 0 : SET_VALUE_DECIMAL_PLACES;
}

/**
 * Whether a set value can be saved: a whole number of at least 1 for reps;
 * above zero with up to three decimal places for the rest.
 */
export function isSetValueValid(field: SetField, value: unknown): boolean {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  if (field === 'reps') return Number.isInteger(value) && value >= 1;
  return value > 0 && hasAtMostDecimalPlaces(value, SET_VALUE_DECIMAL_PLACES);
}

/**
 * A typed set value cut to the decimal places its field allows — a fourth
 * decimal is dropped as it is typed, rather than refused at Save.
 */
export function limitSetValue(field: SetField, value: number): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** getSetValueDecimalPlaces(field);
  // Rounded first to shed float noise (1.005 * 1000 = 1004.99…), then cut.
  return Math.trunc(Math.round(value * factor * 1e6) / 1e6) / factor;
}

/**
 * Get unit label for a field based on measurement system (uppercase for form UI)
 */
export function getFieldUnit(
  field: SetField,
  measurementSystem: MeasurementSystem = 'metric'
): string {
  return SET_FIELD_CONFIGS[field].unit[measurementSystem];
}

export interface TrackingTypeBadge {
  /** Two-letter code shown in the pill (e.g. "WT") */
  code: string;
  /** Full meaning, used as the pill's tooltip / accessible label */
  label: string;
}

/**
 * Compact two-letter code + full meaning for each tracking type, shown as a
 * pill in the sets-column header.
 *
 * Note: "BW" (Bodyweight + Timed) has no corresponding tracking-type value in
 * the current schema, so it is intentionally absent — add it here if/when a
 * `bodyweight_time` tracking type is introduced.
 */
export const TRACKING_TYPE_BADGES: Record<TrackingType, TrackingTypeBadge> = {
  reps_only: { code: 'RE', label: 'Reps only' },
  weight_reps: { code: 'WR', label: 'Weighted + Reps' },
  weight_time: { code: 'WT', label: 'Weighted + Timed' },
  distance_based: { code: 'DI', label: 'Distance only' },
  time_based: { code: 'TI', label: 'Time only' },
  time_distance: { code: 'TD', label: 'Time + Distance' },
};

/**
 * Resolve the header pill (code + meaning) for a tracking type,
 * falling back to "Weighted + Reps" for unknown values.
 */
export function getTrackingTypeBadge(
  trackingType: TrackingType
): TrackingTypeBadge {
  return TRACKING_TYPE_BADGES[trackingType] ?? TRACKING_TYPE_BADGES.weight_reps;
}

/**
 * Build an empty "regular" set seeded only with the fields relevant to the
 * given tracking type (e.g. reps_only -> reps; weight_time -> weight + duration).
 * Shared by every "add set" entry point so new sets never carry fields that
 * don't apply to the exercise. `createId` supplies the set's uuid, since the
 * platforms mint ids differently (`crypto.randomUUID` on web, expo-crypto on
 * mobile).
 */
export function buildEmptySet(
  trackingType: TrackingType,
  createId: () => string
): {
  uuid: string;
  type: 'regular';
  reps?: number;
  weight?: number;
  duration?: number;
  distance?: number;
} {
  const fields = getFieldsForWorkoutExerciseTrackingType(trackingType);
  return {
    uuid: createId(),
    type: 'regular',
    reps: fields.includes('reps') ? 0 : undefined,
    weight: fields.includes('weight') ? 0 : undefined,
    duration: fields.includes('duration') ? 0 : undefined,
    distance: fields.includes('distance') ? 0 : undefined,
  };
}

/**
 * Save state for autosave indicator
 *
 * - draft: Unsaved changes exist
 * - draft_saving: Saving draft to localStorage
 * - draft_saved: Draft saved to localStorage
 * - saving: API save in progress
 * - saved: API save succeeded
 * - error: API save failed
 */
export type SaveState =
  'draft' | 'draft_saving' | 'draft_saved' | 'saving' | 'saved' | 'error';

/**
 * Set scheme presets
 */
export interface SetScheme {
  id: string;
  name: string;
  label: string;
  description?: string;
  sets: WorkoutSet[];
}

/**
 * Weight & Reps schemes (default for strength training)
 */
export const WEIGHT_REPS_SCHEMES: SetScheme[] = [
  {
    id: '3x10',
    name: '3×10',
    label: 'HYPER',
    sets: [
      { reps: 10, weight: 0 },
      { reps: 10, weight: 0 },
      { reps: 10, weight: 0 },
    ],
  },
  {
    id: '5x5',
    name: '5×5',
    label: 'STR',
    sets: [
      { reps: 5, weight: 0 },
      { reps: 5, weight: 0 },
      { reps: 5, weight: 0 },
      { reps: 5, weight: 0 },
      { reps: 5, weight: 0 },
    ],
  },
  {
    id: 'pyramid',
    name: 'PYRAMID',
    label: '12→8',
    sets: [
      { reps: 12, weight: 0 },
      { reps: 10, weight: 0 },
      { reps: 8, weight: 0 },
    ],
  },
  {
    id: 'drop',
    name: 'DROP',
    label: '3× DESC',
    sets: [
      { reps: 10, weight: 0 },
      { reps: 10, weight: 0 },
      { reps: 10, weight: 0 },
    ],
  },
];

/**
 * Reps Only schemes (bodyweight exercises)
 */
export const REPS_ONLY_SCHEMES: SetScheme[] = [
  {
    id: '3x10-reps',
    name: '3×10',
    label: 'STD',
    sets: [{ reps: 10 }, { reps: 10 }, { reps: 10 }],
  },
  {
    id: '3x15-reps',
    name: '3×15',
    label: 'END',
    sets: [{ reps: 15 }, { reps: 15 }, { reps: 15 }],
  },
  {
    id: 'pyramid-reps',
    name: 'PYRAMID',
    label: '15→10',
    sets: [{ reps: 15 }, { reps: 12 }, { reps: 10 }],
  },
  {
    id: 'amrap',
    name: 'AMRAP',
    label: '3×MAX',
    sets: [{ reps: 0 }, { reps: 0 }, { reps: 0 }],
  },
];

/**
 * Time-based schemes (planks, holds, cardio intervals)
 */
export const TIME_BASED_SCHEMES: SetScheme[] = [
  {
    id: '3x30s',
    name: '3×30s',
    label: 'SHORT',
    sets: [{ duration: 30 }, { duration: 30 }, { duration: 30 }],
  },
  {
    id: '3x60s',
    name: '3×60s',
    label: 'STD',
    sets: [{ duration: 60 }, { duration: 60 }, { duration: 60 }],
  },
  {
    id: 'tabata',
    name: 'TABATA',
    label: '8×20s',
    sets: Array(8).fill({ duration: 20 }),
  },
  {
    id: 'pyramid-time',
    name: 'PYRAMID',
    label: '30→60',
    sets: [{ duration: 30 }, { duration: 45 }, { duration: 60 }],
  },
];

/**
 * Distance travels in the user's unit (km or mi) — the API stores kilometres
 * and converts both ways — and every set must be at least 1 (`min:1`), so the
 * distance schemes are whole units, named in the user's unit.
 */
const distanceUnitLabel = (measurementSystem: MeasurementSystem): string =>
  measurementSystem === 'imperial' ? 'mi' : 'km';

/**
 * Distance-based schemes (running, rowing)
 */
export function distanceSchemes(
  measurementSystem: MeasurementSystem = 'metric'
): SetScheme[] {
  const unit = distanceUnitLabel(measurementSystem);
  return [
    {
      id: '3x1',
      name: `3×1${unit}`,
      label: 'SHORT',
      sets: [{ distance: 1 }, { distance: 1 }, { distance: 1 }],
    },
    {
      id: '3x2',
      name: `3×2${unit}`,
      label: 'MID',
      sets: [{ distance: 2 }, { distance: 2 }, { distance: 2 }],
    },
    {
      id: '1x5',
      name: `1×5${unit}`,
      label: 'LONG',
      sets: [{ distance: 5 }],
    },
    {
      id: 'ladder',
      name: 'LADDER',
      label: `1→3${unit}`,
      sets: [{ distance: 1 }, { distance: 2 }, { distance: 3 }],
    },
  ];
}

/**
 * Time & Distance schemes (cardio with pace tracking) — an easy pace of about
 * 6:00/km or 10:00/mi.
 */
export function timeDistanceSchemes(
  measurementSystem: MeasurementSystem = 'metric'
): SetScheme[] {
  const unit = distanceUnitLabel(measurementSystem);
  const perUnit = measurementSystem === 'imperial' ? 600 : 360;
  return [
    {
      id: '3x1-timed',
      name: `3×1${unit}`,
      label: 'REPEATS',
      sets: [
        { duration: perUnit, distance: 1 },
        { duration: perUnit, distance: 1 },
        { duration: perUnit, distance: 1 },
      ],
    },
    {
      id: '1x5-timed',
      name: `1×5${unit}`,
      label: 'STEADY',
      sets: [{ duration: perUnit * 5, distance: 5 }],
    },
  ];
}

/**
 * Weight & Time schemes (weighted holds, carries)
 */
export const WEIGHT_TIME_SCHEMES: SetScheme[] = [
  {
    id: '3x30s-weighted',
    name: '3×30s',
    label: 'HOLD',
    sets: [
      { weight: 0, duration: 30 },
      { weight: 0, duration: 30 },
      { weight: 0, duration: 30 },
    ],
  },
  {
    id: '3x60s-weighted',
    name: '3×60s',
    label: 'CARRY',
    sets: [
      { weight: 0, duration: 60 },
      { weight: 0, duration: 60 },
      { weight: 0, duration: 60 },
    ],
  },
  {
    id: 'pyramid-weighted',
    name: 'PYRAMID',
    label: '30→60',
    sets: [
      { weight: 0, duration: 30 },
      { weight: 0, duration: 45 },
      { weight: 0, duration: 60 },
    ],
  },
];

/**
 * Get schemes for a specific tracking type
 */
export function getSchemesForTrackingType(
  trackingType: TrackingType,
  measurementSystem: MeasurementSystem = 'metric'
): SetScheme[] {
  switch (trackingType) {
    case 'reps_only':
      return REPS_ONLY_SCHEMES;
    case 'weight_reps':
      return WEIGHT_REPS_SCHEMES;
    case 'time_based':
      return TIME_BASED_SCHEMES;
    case 'distance_based':
      return distanceSchemes(measurementSystem);
    case 'time_distance':
      return timeDistanceSchemes(measurementSystem);
    case 'weight_time':
      return WEIGHT_TIME_SCHEMES;
    default:
      return WEIGHT_REPS_SCHEMES;
  }
}

/**
 * @deprecated Use WEIGHT_REPS_SCHEMES instead
 */
export const DEFAULT_SCHEMES = WEIGHT_REPS_SCHEMES;

/** What the save indicator reads for each state. */
export const SAVE_STATE_LABELS: Record<SaveState, string> = {
  draft: 'DRAFT',
  draft_saving: 'SAVING DRAFT',
  draft_saved: 'DRAFT SAVED',
  saving: 'SAVING',
  saved: 'SAVED',
  error: 'ERROR',
};

/** The words of a form's Discard confirmation. */
export interface DiscardDraftCopy {
  title: string;
  message: string;
  /** The button that keeps the edits. */
  keep: string;
  /** The button that throws them away. */
  discard: string;
}

/**
 * What a form's Discard confirmation says: editing a saved thing goes back to
 * its last save, a new one loses its draft for good. `resourceLabel` names the
 * thing ("challenge", "goal").
 */
export function getDiscardDraftCopy(
  isEditing: boolean,
  resourceLabel = 'draft'
): DiscardDraftCopy {
  return {
    title: 'Discard unsaved changes?',
    message: isEditing
      ? `Your changes will be discarded and the ${resourceLabel} will revert to its last saved state.`
      : 'Your draft will be permanently deleted. This cannot be undone.',
    keep: 'Keep Editing',
    discard: 'Discard',
  };
}

/**
 * What a form's save indicator shows. A failed save outranks one in flight,
 * which outranks the draft's own state.
 */
export function resolveSaveState({
  saveError,
  isSaving,
  draftSaveState,
}: {
  /** The last save's failure, if it failed. */
  saveError?: string | null;
  /** A save is in flight. */
  isSaving: boolean;
  draftSaveState: 'idle' | 'saving' | 'saved';
}): SaveState {
  if (saveError) return 'error';
  if (isSaving) return 'saving';
  if (draftSaveState === 'saving') return 'draft_saving';
  if (draftSaveState === 'saved') return 'draft_saved';
  return 'draft';
}

// =============================================================================
// SET OPERATIONS
// =============================================================================

/**
 * The sets a scheme produces, each with a fresh uuid. Applying a scheme
 * replaces an exercise's sets with these.
 */
export function buildSchemeSets(
  scheme: SetScheme,
  createId: () => string
): TemplateSetOutput[] {
  return scheme.sets.map((set) => ({
    ...set,
    uuid: createId(),
    type: 'regular' as const,
  }));
}

/** The sets with one field of one set changed. An index out of range changes nothing. */
export function updateSetField(
  sets: TemplateSetOutput[],
  setIndex: number,
  field: SetField,
  value: number
): TemplateSetOutput[] {
  return sets.map((set, index) =>
    index === setIndex ? { ...set, [field]: value } : set
  );
}

/** The sets without the one at `setIndex`. */
export function removeSetAt(
  sets: TemplateSetOutput[],
  setIndex: number
): TemplateSetOutput[] {
  return sets.filter((_, index) => index !== setIndex);
}
